import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Layers2, Loader2, RefreshCw, X } from 'lucide-react';

import { SectionCard } from '../components/ttq/SectionCard';
import { useActiveSubject } from '../context/ActiveSubjectContext';
import { useAuth } from '../context/AuthContext';
import {
  loadParticleCoverageStats,
  type ParticleCoverageSubjectEntry,
} from '../services/particleCoverageService';
import {
  reviewService,
  type LearningPositionCoverageStat,
  type LearningPositionSpineCoverageNode,
  type LearningPositionSpineCoverageParticleItem,
  type LearningPositionSpineGraphNode,
} from '../services/reviewService';
import {
  formatSpineCoverageCacheUpdatedAt,
  peekSpineCoveragePageCache,
  writeSpineCoveragePageCache,
} from '../services/spineCoverageCacheService';
import {
  computeUploadedTrunkCoverageFallback,
  coverageStatToPercent,
  formatCoverageHeadline,
} from '../services/uploadedTrunkCoverageService';
import { SUBJECT_CONFIG, type SubjectCode } from '../utils/subjectConfig';

type TrunkBar = {
  id: string;
  nodeNo: number;
  label: string;
  /** Bound particles on this trunk (histobar metric). */
  count: number;
  /** Reviewed subset among bound particles on this trunk. */
  reviewedCount: number;
  emptyParticles: boolean;
  unmapped?: boolean;
};

const UNMAPPED_BAR_ID = '__unmapped__';
const BAR_MAX_WIDTH = 280;
const PARTICLE_PAGE_SIZE = 20;

function resolveTrunkLabel(node: LearningPositionSpineCoverageNode): string {
  const moduleLabel = String(node.firstLevelModuleLabel || node.moduleLabel || '').trim();
  const title = String(node.title || '').trim();
  if (moduleLabel && title && moduleLabel !== title) {
    return title;
  }
  return title || moduleLabel || node.nodeId;
}

function toGraphNodesForUploadedFallback(
  nodes: LearningPositionSpineCoverageNode[],
): LearningPositionSpineGraphNode[] {
  return nodes.map((node) => ({
    nodeId: node.nodeId,
    nodeNo: node.nodeNo,
    title: node.title,
    moduleKey: node.moduleKey,
    moduleLabel: node.moduleLabel,
    firstLevelModuleKey: node.firstLevelModuleKey ?? null,
    firstLevelModuleLabel: node.firstLevelModuleLabel ?? null,
    status: 'idle',
    footprintCount: Math.max(0, Number(node.particleCount ?? node.reviewedParticleCount) || 0),
    latestCompletedAt: null,
    families: [],
  }));
}

function formatTrunkDisplayLabel(bar: Pick<TrunkBar, 'label' | 'emptyParticles'>): string {
  return bar.emptyParticles ? `*${bar.label}` : bar.label;
}

function nodeParticleCount(node: LearningPositionSpineCoverageNode): number {
  if (typeof node.particleCount === 'number' && Number.isFinite(node.particleCount)) {
    return Math.max(0, node.particleCount);
  }
  return Math.max(0, Number(node.reviewedParticleCount) || 0);
}

function buildTrunkBars(
  nodes: LearningPositionSpineCoverageNode[],
  unmappedCount: number,
  unmappedReviewedCount: number,
): TrunkBar[] {
  const bars: TrunkBar[] = nodes
    .map((node) => {
      const count = nodeParticleCount(node);
      return {
        id: node.nodeId,
        nodeNo: Number.isFinite(node.nodeNo) ? Number(node.nodeNo) : 0,
        label: resolveTrunkLabel(node),
        count,
        reviewedCount: Math.max(0, Number(node.reviewedParticleCount) || 0),
        emptyParticles: count <= 0,
      };
    })
    .sort((left, right) => (
      right.count - left.count
      || left.nodeNo - right.nodeNo
      || left.label.localeCompare(right.label, 'zh')
    ));

  if (unmappedCount > 0) {
    bars.push({
      id: UNMAPPED_BAR_ID,
      nodeNo: Number.MAX_SAFE_INTEGER,
      label: '未归入当前主干',
      count: unmappedCount,
      reviewedCount: Math.max(0, unmappedReviewedCount),
      emptyParticles: false,
      unmapped: true,
    });
  }

  return bars;
}

export function SpineReviewCoveragePage() {
  const { user } = useAuth();
  const { activeSubject } = useActiveSubject();
  const userId = String(user?.id || '').trim();
  const subject = activeSubject as SubjectCode;
  const subjectLabel = SUBJECT_CONFIG[subject]?.label ?? subject;
  const accent = SUBJECT_CONFIG[subject]?.color ?? '#2563eb';

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nodes, setNodes] = useState<LearningPositionSpineCoverageNode[]>([]);
  const [unmappedBoundCount, setUnmappedBoundCount] = useState(0);
  const [unmappedReviewedCount, setUnmappedReviewedCount] = useState(0);
  const [mappedBoundCount, setMappedBoundCount] = useState(0);
  const [boundParticleCount, setBoundParticleCount] = useState(0);
  const [spineReviewedCount, setSpineReviewedCount] = useState(0);
  const [subjectCoverage, setSubjectCoverage] = useState<ParticleCoverageSubjectEntry | null>(null);
  const [uploadedTrunkCoverage, setUploadedTrunkCoverage] = useState<LearningPositionCoverageStat | null>(null);
  const [cacheUpdatedAt, setCacheUpdatedAt] = useState<string | null>(null);

  const [drillBar, setDrillBar] = useState<TrunkBar | null>(null);
  const [drillPage, setDrillPage] = useState(1);
  const [drillTotal, setDrillTotal] = useState(0);
  const [drillItems, setDrillItems] = useState<LearningPositionSpineCoverageParticleItem[]>([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState<string | null>(null);

  const applyCacheSnapshot = useCallback((cached: NonNullable<ReturnType<typeof peekSpineCoveragePageCache>>) => {
    setNodes(cached.nodes);
    setUnmappedBoundCount(cached.unmappedBoundCount);
    setUnmappedReviewedCount(cached.unmappedReviewedCount);
    setMappedBoundCount(cached.mappedBoundCount);
    setBoundParticleCount(cached.boundParticleCount);
    setSpineReviewedCount(cached.spineReviewedCount);
    setSubjectCoverage(cached.subjectCoverage);
    setUploadedTrunkCoverage(cached.uploadedTrunkCoverage);
    setCacheUpdatedAt(cached.updatedAt);
  }, []);

  const load = useCallback(async (options?: { background?: boolean }) => {
    if (!userId) return;
    const background = Boolean(options?.background);
    const cached = peekSpineCoveragePageCache(userId, subject);
    if (cached && !background) {
      applyCacheSnapshot(cached);
      setLoading(false);
      setRefreshing(true);
    } else if (!background) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setError(null);

    let nextNodes: LearningPositionSpineCoverageNode[] = [];
    let nextUnmappedBound = 0;
    let nextUnmappedReviewed = 0;
    let nextMappedBound = 0;
    let nextBound = 0;
    let nextSpineReviewed = 0;
    let nextSubjectCoverage: ParticleCoverageSubjectEntry | null = null;
    let nextUploaded: LearningPositionCoverageStat | null = null;

    try {
      const summary = await reviewService.loadLearningPositionSpineCoverage(userId, subject);
      nextNodes = Array.isArray(summary?.nodes) ? summary.nodes : [];
      setNodes(nextNodes);
      const unmappedBound = Number(summary?.unmappedBoundParticleCount);
      nextUnmappedBound = Number.isFinite(unmappedBound)
        ? Math.max(0, unmappedBound)
        : Math.max(0, Number(summary?.unmappedReviewedParticleCount) || 0);
      nextUnmappedReviewed = Math.max(0, Number(summary?.unmappedReviewedParticleCount) || 0);
      nextMappedBound = Math.max(0, Number(summary?.mappedBoundParticleCount) || 0);
      nextBound = Math.max(0, Number(summary?.boundParticleCount) || 0);
      nextSpineReviewed = Math.max(0, Number(summary?.reviewedParticleCount) || 0);
      setUnmappedBoundCount(nextUnmappedBound);
      setUnmappedReviewedCount(nextUnmappedReviewed);
      setMappedBoundCount(nextMappedBound);
      setBoundParticleCount(nextBound);
      setSpineReviewedCount(nextSpineReviewed);
    } catch (coverageError) {
      setError(coverageError instanceof Error ? coverageError.message : '加载学科主干覆盖失败');
    }

    try {
      const coverage = await loadParticleCoverageStats(userId);
      const subjectEntry = coverage.bySubject.find((entry) => entry.subject === subject);
      nextSubjectCoverage = subjectEntry ?? {
        subject,
        totalParticles: 0,
        reviewedParticles: 0,
        coveragePercent: 0,
        sessionCount: 0,
        footprintCount: 0,
      };
      setSubjectCoverage(nextSubjectCoverage);
    } catch {
      // soft-fail
    }

    try {
      nextUploaded = await computeUploadedTrunkCoverageFallback(
        userId,
        subject,
        toGraphNodesForUploadedFallback(nextNodes),
      );
      setUploadedTrunkCoverage(nextUploaded);
    } catch {
      nextUploaded = { covered: 0, total: nextNodes.length, rate: 0 };
      setUploadedTrunkCoverage(nextUploaded);
    }

    if (nextNodes.length > 0 || nextBound > 0 || nextSubjectCoverage || nextUploaded) {
      const written = writeSpineCoveragePageCache(userId, subject, {
        nodes: nextNodes,
        unmappedBoundCount: nextUnmappedBound,
        unmappedReviewedCount: nextUnmappedReviewed,
        mappedBoundCount: nextMappedBound,
        boundParticleCount: nextBound,
        spineReviewedCount: nextSpineReviewed,
        subjectCoverage: nextSubjectCoverage,
        uploadedTrunkCoverage: nextUploaded,
      });
      setCacheUpdatedAt(written.updatedAt);
    }

    setLoading(false);
    setRefreshing(false);
  }, [applyCacheSnapshot, subject, userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadDrillPage = useCallback(async (bar: TrunkBar, page: number) => {
    if (!userId) return;
    setDrillLoading(true);
    setDrillError(null);
    try {
      const data = await reviewService.loadLearningPositionSpineCoverageParticles(
        userId,
        subject,
        bar.id,
        { page, pageSize: PARTICLE_PAGE_SIZE },
      );
      setDrillItems(Array.isArray(data?.items) ? data.items : []);
      setDrillTotal(Math.max(0, Number(data?.total) || 0));
      setDrillPage(Math.max(1, Number(data?.page) || page));
    } catch (err) {
      setDrillError(err instanceof Error ? err.message : '加载知识颗粒列表失败');
      setDrillItems([]);
      setDrillTotal(0);
    } finally {
      setDrillLoading(false);
    }
  }, [subject, userId]);

  const openDrill = useCallback((bar: TrunkBar) => {
    if (bar.count <= 0) return;
    setDrillBar(bar);
    setDrillPage(1);
    setDrillItems([]);
    setDrillTotal(bar.count);
    void loadDrillPage(bar, 1);
  }, [loadDrillPage]);

  const closeDrill = useCallback(() => {
    setDrillBar(null);
    setDrillError(null);
    setDrillItems([]);
  }, []);

  const bars = useMemo(
    () => buildTrunkBars(nodes, unmappedBoundCount, unmappedReviewedCount),
    [nodes, unmappedBoundCount, unmappedReviewedCount],
  );
  const maxCount = useMemo(
    () => Math.max(1, bars.reduce((max, bar) => Math.max(max, bar.count), 0)),
    [bars],
  );

  const hasEmptyMarked = bars.some((bar) => bar.emptyParticles);
  const hasUnmappedBar = unmappedBoundCount > 0;
  const allPossessedTrunksCovered = bars.some((bar) => !bar.unmapped) && !hasEmptyMarked;

  const learningLoading = loading && !subjectCoverage;
  const uploadedLoading = loading && !uploadedTrunkCoverage;

  const reviewedCount = subjectCoverage?.reviewedParticles ?? 0;
  const totalCount = subjectCoverage?.totalParticles ?? 0;
  const coveragePercent = subjectCoverage?.coveragePercent ?? null;
  const coverageHeadline = formatCoverageHeadline(coveragePercent, learningLoading);

  const uploadedCoveredTrunks = uploadedTrunkCoverage?.covered ?? null;
  const uploadedTotalTrunks = uploadedTrunkCoverage?.total ?? (bars.length > 0 ? bars.length : null);
  const uploadedCoveragePercent = coverageStatToPercent(uploadedTrunkCoverage);
  const uploadedCoverageHeadline = formatCoverageHeadline(uploadedCoveragePercent, uploadedLoading);

  const drillPageCount = Math.max(1, Math.ceil(drillTotal / PARTICLE_PAGE_SIZE));
  const canDrillPrev = drillPage > 1;
  const canDrillNext = drillPage < drillPageCount;
  const cacheUpdatedLabel = formatSpineCoverageCacheUpdatedAt(cacheUpdatedAt);

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <Link
            to="/"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-ink hover:bg-black/[0.04]"
            aria-label="返回首页"
          >
            <ArrowLeft size={16} />
          </Link>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-ink">学科主干覆盖</h1>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
              <p className="text-sm text-muted">{subjectLabel} · 各主干绑定分布</p>
              {cacheUpdatedLabel ? (
                <p className="text-xs tabular-nums text-subtle">缓存更新 {cacheUpdatedLabel}</p>
              ) : null}
            </div>
          </div>
        </div>
        <button
          type="button"
          disabled={loading || refreshing}
          onClick={() => { void load({ background: true }); }}
          className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border bg-surface px-3 text-sm font-medium text-ink hover:bg-black/[0.04] disabled:opacity-60"
        >
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : undefined} />
          刷新
        </button>
      </div>

      <SectionCard tone="accent">
        <div className="flex items-center gap-4">
          <p className="min-w-[4.5rem] text-3xl font-extrabold tabular-nums leading-none" style={{ color: accent }}>
            {coverageHeadline}
          </p>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink">当前学习覆盖度</p>
            <p className="mt-0.5 text-xs text-muted">
              {learningLoading
                ? '--% = 已复习 -- / 全部知识颗粒 --'
                : `${Math.round(coveragePercent ?? 0)}% = 已复习 ${reviewedCount} / 全部知识颗粒 ${totalCount}`}
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-4 border-t border-border/60 pt-4">
          <p className="min-w-[4.5rem] text-2xl font-extrabold tabular-nums leading-none" style={{ color: accent }}>
            {uploadedCoverageHeadline}
          </p>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink">学科覆盖度</p>
            <p className="mt-0.5 text-xs text-muted">
              {uploadedLoading
                ? '--% = 已覆盖主干 -- / 全部学科主干 --'
                : `${Math.round(uploadedCoveragePercent ?? 0)}% = 已覆盖主干 ${uploadedCoveredTrunks ?? 0} / 全部学科主干 ${uploadedTotalTrunks ?? 0}`}
            </p>
          </div>
        </div>
      </SectionCard>

      <div className="space-y-1.5 px-1 text-xs leading-5 text-muted">
        <p>纵轴：学习主干 · 横轴：已绑定知识颗粒数（每个颗粒只归入一根柱，已去重）</p>
        <p>
          柱长 = 绑定颗粒；卡片数 = 已复习，或已入品题且开启「品题加入复习列」的颗粒（各计 1）。
        </p>
        {!loading ? (
          <p>
            柱图合计：已绑定归入 {mappedBoundCount}
            {unmappedBoundCount > 0 ? ` + 未归入 ${unmappedBoundCount}` : ''}
            {boundParticleCount > 0 ? ` = ${boundParticleCount}` : ''}
            {spineReviewedCount > 0 ? ` · 卡片 ${spineReviewedCount}` : ''}
          </p>
        ) : null}
        {hasUnmappedBar ? (
          <p>
            「未归入当前主干」{unmappedBoundCount}：已绑定但尚未归入当前学科主干图的知识颗粒
          </p>
        ) : null}
        {hasEmptyMarked ? <p>* 尚无已绑定知识颗粒的主干</p> : null}
        {allPossessedTrunksCovered ? (
          <p className="font-medium text-ink">
            所有已具备的学科主干都已覆盖到，可以继续丰富更多学科主干
          </p>
        ) : null}
      </div>

      {loading && bars.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-16">
          <Loader2 className="animate-spin text-primary" size={28} />
          <p className="text-sm text-muted">正在加载学科主干覆盖…</p>
        </div>
      ) : null}

      {error && bars.length === 0 ? (
        <SectionCard tone="muted">
          <p className="text-sm text-danger">{error}</p>
        </SectionCard>
      ) : null}

      {!loading && bars.length === 0 && !error ? (
        <SectionCard tone="muted">
          <p className="text-sm text-muted">暂无学科主干覆盖数据</p>
        </SectionCard>
      ) : null}

      {bars.length > 0 ? (
        <SectionCard>
          {error ? <p className="mb-3 text-xs text-danger">{error}</p> : null}
          <div className="space-y-2.5">
            {bars.map((bar) => {
              const width = Math.max(bar.count > 0 ? 8 : 2, Math.round((bar.count / maxCount) * BAR_MAX_WIDTH));
              const clickable = bar.count > 0;
              return (
                <div key={bar.id} className="flex items-center gap-3">
                  <p className="w-28 shrink-0 truncate text-xs font-semibold text-ink sm:w-36" title={formatTrunkDisplayLabel(bar)}>
                    {formatTrunkDisplayLabel(bar)}
                  </p>
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <button
                      type="button"
                      disabled={!clickable}
                      onClick={() => openDrill(bar)}
                      className={
                        clickable
                          ? 'flex min-w-0 flex-1 items-center gap-2 rounded-md text-left transition hover:bg-black/[0.03] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
                          : 'flex min-w-0 flex-1 cursor-default items-center gap-2'
                      }
                      aria-label={
                        clickable
                          ? `查看「${bar.label}」的 ${bar.count} 个已绑定知识颗粒（卡片 ${bar.reviewedCount}）`
                          : undefined
                      }
                    >
                      <div
                        className={
                          bar.unmapped
                            ? 'h-3 shrink-0 rounded-sm bg-amber-500'
                            : bar.emptyParticles
                              ? 'h-3 shrink-0 rounded-sm bg-border'
                              : 'h-3 shrink-0 rounded-sm'
                        }
                        style={{
                          width,
                          backgroundColor: bar.unmapped || bar.emptyParticles ? undefined : accent,
                        }}
                      />
                      <span
                        className={
                          clickable
                            ? 'inline-flex shrink-0 items-center gap-1.5 text-xs font-semibold tabular-nums text-primary underline-offset-2 hover:underline'
                            : 'inline-flex shrink-0 items-center gap-1.5 text-xs font-semibold tabular-nums text-muted'
                        }
                      >
                        {bar.count}
                        {bar.count > 0 ? (
                          <span
                            className="inline-flex items-center gap-0.5 font-medium text-muted no-underline"
                            title={`卡片 ${bar.reviewedCount}（已复习或已入品题复习）`}
                          >
                            <Layers2 size={12} aria-hidden />
                            <span className="tabular-nums">{bar.reviewedCount}</span>
                          </span>
                        ) : null}
                      </span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      ) : null}

      {drillBar ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 p-4 sm:items-center">
          <button type="button" className="absolute inset-0" aria-label="关闭" onClick={closeDrill} />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="spine-coverage-particles-title"
            className="relative z-10 flex max-h-[min(80vh,560px)] w-full max-w-lg flex-col rounded-2xl border border-border bg-surface shadow-xl"
          >
            <div className="flex items-start justify-between gap-3 border-b border-border/60 px-4 py-3">
              <div className="min-w-0">
                <h2 id="spine-coverage-particles-title" className="truncate text-sm font-semibold text-ink">
                  {drillBar.label}
                </h2>
                <p className="mt-0.5 text-xs text-muted">
                  共 {drillTotal} 个知识颗粒
                  {drillBar.unmapped ? ' · 未归入当前主干' : ''}
                </p>
              </div>
              <button
                type="button"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted hover:bg-black/[0.04]"
                onClick={closeDrill}
                aria-label="关闭列表"
              >
                <X size={16} />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
              {drillLoading && drillItems.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-10">
                  <Loader2 className="animate-spin text-primary" size={22} />
                  <p className="text-xs text-muted">加载中…</p>
                </div>
              ) : null}

              {drillError ? (
                <p className="text-sm text-danger">{drillError}</p>
              ) : null}

              {!drillLoading && !drillError && drillItems.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted">暂无知识颗粒</p>
              ) : null}

              {drillItems.length > 0 ? (
                <ul className="divide-y divide-border/50">
                  {drillItems.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/knowledge/${encodeURIComponent(item.id)}`}
                        className="flex items-start justify-between gap-3 py-2.5 hover:bg-black/[0.02]"
                        onClick={closeDrill}
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-ink">
                            {item.title?.trim() || '未命名知识颗粒'}
                          </p>
                          <p className="mt-0.5 truncate font-mono text-[11px] text-muted">{item.id}</p>
                        </div>
                        <span
                          className={
                            item.reviewed
                              ? 'shrink-0 rounded-md bg-emerald-500/10 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700'
                              : 'shrink-0 rounded-md bg-black/[0.04] px-1.5 py-0.5 text-[11px] font-medium text-muted'
                          }
                        >
                          {item.reviewed ? '已复习' : '未复习'}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            {drillTotal > PARTICLE_PAGE_SIZE ? (
              <div className="flex items-center justify-between gap-3 border-t border-border/60 px-4 py-2.5">
                <button
                  type="button"
                  disabled={!canDrillPrev || drillLoading}
                  onClick={() => {
                    if (!drillBar || !canDrillPrev) return;
                    void loadDrillPage(drillBar, drillPage - 1);
                  }}
                  className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2.5 text-xs font-medium text-ink hover:bg-black/[0.04] disabled:opacity-40"
                >
                  <ChevronLeft size={14} />
                  上一页
                </button>
                <p className="text-xs tabular-nums text-muted">
                  {drillPage} / {drillPageCount}
                </p>
                <button
                  type="button"
                  disabled={!canDrillNext || drillLoading}
                  onClick={() => {
                    if (!drillBar || !canDrillNext) return;
                    void loadDrillPage(drillBar, drillPage + 1);
                  }}
                  className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2.5 text-xs font-medium text-ink hover:bg-black/[0.04] disabled:opacity-40"
                >
                  下一页
                  <ChevronRight size={14} />
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
